import { type MotdModel } from "../models";
import { type MotdRuleModel } from "./models";

import { pageTags, isCheckoutPage } from "../page-tags";

export function motdAppliesToPage(target: MotdModel) {
    if (isCheckoutPage) return false;
    const auditedTagIds: number[] = [];
    if (location.search != "" && "URLSearchParams" in window) {
        const params = new window.URLSearchParams(location.search);
        for (const key of params.keys()) {
            if (key.toUpperCase() == "TESTBANNER")
                if (params.getAll(key).some((v) => v == target.id.toString()))
                    return true;
            if (key.toUpperCase() == "FORCEMOTD") {
                auditedTagIds.push(
                    ...params
                        .getAll(key)
                        .map((x) => parseInt(x))
                        .filter((x) => !isNaN(x))
                );
            }
        }
    }
    const audit = auditedTagIds.includes(target.id);
    const now = new Date();
    if (target.visibleFromUtc != null) {
        const from = new Date(target.visibleFromUtc);
        if (from.getTime() > now.getTime()) {
            if (audit)
                console.log(
                    `motdId:${target.id} 👎 not matching by visibleFrom`
                );
            return false;
        }
    }

    if (target.visibleToUtc != null) {
        const to = new Date(target.visibleToUtc);
        if (to.getTime() < now.getTime()) {
            if (audit)
                console.log(`motdId:${target.id} 👎 not matching by visibleTo`);
            return false;
        }
    }

    const ruleSets = target.ruleSets;

    if (ruleSets.length === 0) {
        if (audit) console.log(`motdId:${target.id} 👍 has no rules`);
        return true;
    }

    for (const ruleSet of ruleSets) {
        if (
            ruleSetAppliesToPage(
                ruleSet,
                audit
                    ? `motdId:${target.id} ruleSet:${ruleSets.indexOf(ruleSet)}`
                    : undefined
            )
        ) {
            if (audit)
                console.log(
                    `motdId:${
                        target.id
                    } 👍 matches ruleset at index ${ruleSets.indexOf(ruleSet)}`
                );
            return true;
        }
    }

    if (audit) console.log(`motdId:${target.id} 👎 no rules applies to page`);
    return false;
}

function getUrlPath() {
    return window.location.pathname.toUpperCase();
}

function stringifyRule(rule: MotdRuleModel) {
    if (rule.ruleType == "UrlPath") {
        return (
            rule.ruleType +
            " (" +
            getUrlPath() +
            ") " +
            rule.pathOperator +
            " " +
            rule.input
        );
    }
    if (rule.ruleType == "PageTags") {
        return (
            rule.ruleType +
            " " +
            rule.key +
            " " +
            rule.pathOperator +
            " " +
            rule.input
        );
    }
    if (rule.ruleType == "PageContext") {
        return (
            rule.ruleType + " " + rule.pageContextOperator + " " + rule.input
        );
    }
    return "Unknown rule: " + rule.ruleType;
}

function ruleSetAppliesToPage(
    ruleSet: MotdRuleModel[],
    logPrefix: string | undefined
) {
    for (const rule of ruleSet) {
        const matchesRule = ruleAppliesToPage(rule, logPrefix);
        if (!matchesRule) {
            if (logPrefix)
                console.log(
                    `${logPrefix} 👎 rule ${stringifyRule(rule)} did not match`
                );
            return false;
        } else {
            if (logPrefix)
                console.log(
                    `${logPrefix} 👍 rule ${stringifyRule(rule)} did match`
                );
        }
    }
    return true;
}

function flipByInvert(v: boolean, invert: boolean) {
    return invert ? !v : v;
}

function ruleAppliesToPage(rule: MotdRuleModel, logPrefix: string | undefined) {
    const invert = rule.invert;
    if (rule.ruleType === "PageTags") {
        const match = pageTags.find((x) => x.tagName == rule.key);
        const input = rule.input.toUpperCase();
        switch (rule.tagOperator) {
            case "Contains":
                return flipByInvert(
                    match != null &&
                        match.value != null &&
                        match.value.toUpperCase().indexOf(input) >= 0,
                    invert
                );
            case "EndsWith":
                return flipByInvert(
                    match != null &&
                        match.value != null &&
                        match.value.toUpperCase().endsWith(input),
                    invert
                );
            case "StartsWith":
                return flipByInvert(
                    match != null &&
                        match.value != null &&
                        match.value.toUpperCase().startsWith(input),
                    invert
                );
            case "Equals":
                return flipByInvert(
                    match != null &&
                        match.value != null &&
                        match.value.toUpperCase() === input,
                    invert
                );
            case "IsDefined":
                return flipByInvert(
                    match != null && match.value != null,
                    invert
                );
            default:
                return true;
        }
    }
    if (rule.ruleType === "UrlPath") {
        const path = getUrlPath();
        const input = rule.input.toUpperCase();
        switch (rule.pathOperator) {
            case "Contains":
                return flipByInvert(path.indexOf(input) >= 0, invert);
            case "EndsWith":
                return flipByInvert(path.endsWith(input), invert);
            case "StartsWith":
                return flipByInvert(path.startsWith(input), invert);
            case "Equals":
                return flipByInvert(path === input, invert);
            case "SubPageOf": {
                let root = input;
                while (root.endsWith("/"))
                    root = root.substr(0, root.length - 1);
                return flipByInvert(path.startsWith(root + "/"), invert);
            }
            default:
                return true;
        }
    }
    if (rule.ruleType === "PageContext") {
        const context = window.odendo.pageContext;
        const input = rule.input;
        switch (rule.pageContextOperator) {
            case "Equals":
                return flipByInvert(context === input, invert);
            default:
                return true;
        }
    }
    return true;
}
